import * as Yup from 'yup';

import i18n from '../../../../i18n';

function USZipValidator(value: string) {
  if (!value) {
    return i18n.t('checkout.fieldErrors.missingZipCode');
  }
  if (!/^\d{5}(-\d{4})?$/.test(value)) {
    return i18n.t('checkout.fieldErrors.invalidZipCode');
  }
  return '';
}

function CAZipValidator(value: string) {
  if (!value) {
    return i18n.t('checkout.fieldErrors.missingPostalCode');
  }
  if (!/^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/.test(value)) {
    return i18n.t('checkout.fieldErrors.invalidPostalCode');
  }
  return '';
}

function NZZipValidator(value: string) {
  if (!value) {
    return i18n.t('checkout.fieldErrors.missingPostalCode');
  }
  if (!/^\d{4}$/.test(value)) {
    return i18n.t('checkout.fieldErrors.invalidPostalCode');
  }
  return '';
}

function UKZipValidator(value: string) {
  if (!value) {
    return i18n.t('checkout.fieldErrors.missingPostalCode');
  }
  if (!/^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i.test(value)) {
    return i18n.t('checkout.fieldErrors.invalidPostalCode');
  }
  return '';
}

function AUZipValidator(value: string) {
  if (!value) {
    return i18n.t('checkout.fieldErrors.missingPostalCode');
  }
  if (!/^\d{4}$/.test(value)) {
    return i18n.t('checkout.fieldErrors.invalidPostalCode');
  }
  return '';
}

function getZipValidator(countryCode2: string) {
  switch (countryCode2.toUpperCase()) {
    case 'CA':
      return CAZipValidator;
    case 'GB':
    case 'UK':
      return UKZipValidator;
    case 'NZ':
      return NZZipValidator;
    case 'AU':
      return AUZipValidator;
    default:
      return USZipValidator;
  }
}

const MIN_CHARACTERS_FOR_SEARCH = 3;
const MIN_ADDRESS_REQUIRED_LENGTH = 7;

const getValidationSchema = (countryCode2 = '', zipOnly?: boolean) =>
  Yup.object({
    ...(zipOnly
      ? {}
      : {
          address: Yup.string()
            .required(i18n.t('checkout.fieldErrors.addressRequired'))
            .min(MIN_ADDRESS_REQUIRED_LENGTH, i18n.t('checkout.fieldErrors.invalidAddress')),
        }),
    zipCode: Yup.string()
      .required(
        countryCode2 === 'US'
          ? i18n.t('checkout.fieldErrors.zipCodeRequired')
          : i18n.t('checkout.fieldErrors.postalCodeRequired')
      )
      .test('local-validation', '', (value, context) => {
        const validator = getZipValidator(countryCode2);
        const errorMessage = validator(value || '');

        if (errorMessage) {
          return context.createError({ message: errorMessage });
        }
        return !errorMessage;
      }),
  });

interface GetInitialErrorsParams {
  isTaxValid: boolean;
  zipOnly: boolean;
  fullAddress: string;
  countryCode2: string;
  zipCode: string;
}

const getInitialErrors = ({
  isTaxValid,
  zipOnly,
  fullAddress,
  countryCode2,
  zipCode,
}: GetInitialErrorsParams) => {
  const validationSchema = getValidationSchema(countryCode2);
  let zipError = '';
  let addressError = '';

  try {
    validationSchema.validateSyncAt('zipCode', { zipCode });
  } catch (error: any) {
    zipError = error.message;
  }
  try {
    if (!zipOnly) {
      validationSchema.validateSyncAt('address', { address: fullAddress });
    }
  } catch (error: any) {
    addressError = error.message;
  }

  return {
    zipCode: isTaxValid ? '' : zipError,
    ...(zipOnly ? {} : { address: addressError }),
  };
};

const getInitialValid = ({
  isTaxValid,
  zipOnly,
  fullAddress,
  countryCode2,
  zipCode,
}: GetInitialErrorsParams) => {
  const initialErrors = getInitialErrors({
    isTaxValid,
    zipOnly,
    fullAddress,
    countryCode2,
    zipCode,
  });
  return Object.values(initialErrors).every((error) => error === '');
};

export const AddressElementUtils = {
  getInitialValid,
  getInitialErrors,
  getValidationSchema,
  MIN_CHARACTERS_FOR_SEARCH,
};
